import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
} from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
);

const AdminDashboard = ({ onNavigate, userInfo = {} }) => {
  // Mobile state for content responsiveness
  const [isMobile, setIsMobile] = useState(false);

  // API Data States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dashboard Data States - UPDATED WITH DETAILED ATTENDANCE
  const [dashboardStats, setDashboardStats] = useState({
    totalStudents: 0,
    totalTeachers: 0,
    totalHODs: 0,
    pendingGrievances: 0,
    // Updated attendance fields
    studentsPresent: 0,
    totalStudentsToday: 0,
    teachersPresent: 0,
    totalTeachersToday: 0,
    studentAttendancePercentage: 0,
    teacherAttendancePercentage: 0,
    activeNotices: 0,
    labEquipment: 0,
    internships: 0,
    pendingRequisitions: 0,
    serverStatus: 'Unknown',
    dbStatus: 'Unknown'
  });

  const [recentActivities, setRecentActivities] = useState([]);
  const [systemAlerts, setSystemAlerts] = useState([]);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch Admin Dashboard Data
  useEffect(() => {
    const fetchAdminDashboard = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (!token) {
          setError('Authentication token not found. Please login again.');
          setLoading(false);
          return;
        }

        const apiUrl = `${API_LINK}/api/admin/dashboard`;

        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          if (response.status === 401) {
            setError('Session expired. Please login again.');
            handleLogout();
            return;
          } else if (response.status === 404) {
            setError('API endpoint not found. Please check server configuration.');
            setLoading(false);
            return;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          setError('Server returned non-JSON response. Please check API configuration.');
          setLoading(false);
          return;
        }

        const result = await response.json();

        if (result.success && result.data) {
          const data = result.data;

          // Updated dashboard stats - UPDATED WITH CORRECT FIELD MAPPING
          setDashboardStats({
            totalStudents: data.stats?.totalStudents || 0,
            totalTeachers: data.stats?.totalTeachers || 0,
            totalHODs: data.stats?.totalHODs || 0,
            pendingGrievances: data.stats?.pendingGrievances || 0,
            // Updated attendance fields - using database totals
            studentsPresent: data.stats?.studentsPresent || 0,
            totalStudentsToday: data.stats?.totalStudentsToday || data.stats?.totalStudents || 0, // Total students in database
            teachersPresent: data.stats?.teachersPresent || 0,
            totalTeachersToday: data.stats?.totalTeachersToday || data.stats?.totalTeachers || 0, // Total teachers in database
            studentAttendancePercentage: data.stats?.studentAttendancePercentage || 0,
            teacherAttendancePercentage: data.stats?.teacherAttendancePercentage || 0,
            activeNotices: data.stats?.activeNotices || 0,
            labEquipment: data.stats?.labEquipment || 0,
            internships: data.stats?.internships || 0,
            pendingRequisitions: data.stats?.pendingRequisitions || 0,
            serverStatus: data.system?.serverStatus || 'Running',
            dbStatus: data.system?.dbStatus || 'Connected'
          });

          // Update other data
          setRecentActivities(data.recentActivities || []);
          setSystemAlerts(data.systemAlerts || []);

        } else {
          setError(result.message || 'Failed to fetch dashboard data');
        }

      } catch (error) {
        console.error('Error fetching admin dashboard data:', error);

        if (error.name === 'SyntaxError' && error.message.includes('Unexpected token')) {
          setError('Server returned HTML instead of JSON. Please check if the API endpoint exists.');
        } else if (error.message.includes('fetch')) {
          setError('Cannot connect to server. Please ensure the server is running');
        } else {
          setError(`Failed to load dashboard data: ${error.message}`);
        }
      } finally {
        setLoading(false);
      }
    };

    // Add a small delay to ensure component is mounted
    const timer = setTimeout(fetchAdminDashboard, 500);
    return () => clearTimeout(timer);
  }, []);

  // Handle logout (kept for API 401 response and error state)
  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token) {
      fetch(`${API_LINK}/api/auth/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }).catch(error => console.error('Logout API error:', error));
    }

    if (onNavigate) {
      onNavigate('home');
    } else {
      window.location.href = '/';
    }
  };

  // Navigation helper for content buttons (maps labels to paths)
  const navigateTo = (label) => {
    if (!onNavigate) return;
    const navMap = {
      'Student Management': 'admin-students',
      'Teacher Management': 'admin-teachers',
      'Attendance Records': 'admin-attendance',
      'Teacher Attendance': 'admin-teacher-attendance',
      'Grievance Portal': 'admin-grievances',
      'HOD Portal': 'admin-hods',
      'Internship Portal': 'admin-internships',
      'Lab Management': 'admin-labs',
      'Requisition': 'admin-requisition',
      'Training Management': 'admin-training',
      'Login Portal': 'admin-login-credentials',
      'Login Logs': 'admin-login-logs',
      'Student Cards': 'admin-student-cards',
      'Timetable': 'admin-timetable',
      'Assign Marks': 'admin-marks',
      'Notice Management': 'admin-notices',
      'Manage Subjects': 'admin-attendance-subjects',
      'Push Notifications': 'admin-notifications'
    };
    if (navMap[label]) onNavigate(navMap[label]);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Loading Component
  if (loading) {
    return (
      <Layout role="admin" onNavigate={onNavigate} activePage="admin-dashboard" userInfo={userInfo}>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '60vh',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '4px solid #e3f2fd',
            borderTop: '4px solid #238548',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading Admin Portal...</p>
        </div>
      </Layout>
    );
  }

  // Error Component
  if (error) {
    return (
      <Layout role="admin" onNavigate={onNavigate} activePage="admin-dashboard" userInfo={userInfo}>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '60vh',
          flexDirection: 'column',
          gap: '1rem',
          padding: '2rem'
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            padding: '3rem',
            borderRadius: '12px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            textAlign: 'center',
            maxWidth: '400px',
            border: '1px solid #e0e0e0'
          }}>
            <div style={{
              fontSize: '4rem',
              color: '#ff6b6b',
              marginBottom: '1.5rem'
            }}>
              ⚠️
            </div>

            <h3 style={{
              color: '#333',
              margin: '0 0 1rem 0',
              fontSize: '1.4rem',
              fontWeight: 600
            }}>
              Unable to Load Admin Portal
            </h3>

            <p style={{
              color: '#666',
              margin: '0 0 2rem 0',
              fontSize: '1rem',
              lineHeight: 1.5
            }}>
              {error}
            </p>

            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={() => window.location.reload()}
                style={{
                  backgroundColor: '#238548',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: 500,
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#1e7139';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#238548';
                }}
              >
                Try Again
              </button>

              <button
                onClick={handleLogout}
                style={{
                  backgroundColor: '#666',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: 500,
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#555';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#666';
                }}
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout role="admin" onNavigate={onNavigate} activePage="admin-dashboard" userInfo={userInfo}>
      {/* Welcome Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
        color: '#ffffff',
        padding: '2rem',
        borderRadius: '12px',
        marginBottom: '2rem',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem' }}>
          Welcome back, Administrator! 👋
        </h2>
        <p style={{ margin: 0, opacity: 0.9 }}>
          You have complete control over the SVU Learning Hub system. Monitor, manage, and maintain all university operations from this central portal.
        </p>
      </div>

      {/* Quick Action Buttons */}
      <section style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <button
          onClick={() => navigateTo('Push Notifications')}
          style={{
            background: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)',
            color: '#ffffff',
            border: 'none',
            padding: '1.5rem',
            borderRadius: '12px',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '500',
            boxShadow: '0 4px 15px rgba(139, 92, 246, 0.3)',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-3px)';
            e.target.style.boxShadow = '0 6px 20px rgba(139, 92, 246, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 15px rgba(139, 92, 246, 0.3)';
          }}
        >
          <span style={{ fontSize: '1.5rem' }}>🔔</span>
          <span>Send Push Notification</span>
        </button>

        <button
          onClick={() => navigateTo('Student Management')}
          style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
            color: '#ffffff',
            border: 'none',
            padding: '1.5rem',
            borderRadius: '12px',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '500',
            boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-3px)';
            e.target.style.boxShadow = '0 6px 20px rgba(59, 130, 246, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 15px rgba(59, 130, 246, 0.3)';
          }}
        >
          <span style={{ fontSize: '1.5rem' }}>👥</span>
          <span>Manage Students</span>
        </button>

        <button
          onClick={() => navigateTo('Attendance Records')}
          style={{
            background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
            color: '#ffffff',
            border: 'none',
            padding: '1.5rem',
            borderRadius: '12px',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '500',
            boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-3px)';
            e.target.style.boxShadow = '0 6px 20px rgba(16, 185, 129, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 15px rgba(16, 185, 129, 0.3)';
          }}
        >
          <span style={{ fontSize: '1.5rem' }}>📝</span>
          <span>View Attendance</span>
        </button>
      </section>

      {/* Stats Cards - UPDATED WITH DETAILED ATTENDANCE */}
      <section style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        {[
          {
            icon: '👥',
            title: 'Total Students',
            value: dashboardStats.totalStudents,
            subtitle: 'Active Enrollments',
            color: '#3b82f6'
          },
          {
            icon: '👨‍🏫',
            title: 'Total Teachers',
            value: dashboardStats.totalTeachers,
            subtitle: 'Faculty Members',
            color: '#10b981'
          },
          {
            icon: '👤',
            title: 'HODs',
            value: dashboardStats.totalHODs,
            subtitle: 'Department Heads',
            color: '#8b5cf6'
          },
          {
            icon: '❗',
            title: 'Pending Grievances',
            value: dashboardStats.pendingGrievances,
            subtitle: 'Requires Attention',
            color: '#ef4444'
          },
          {
            icon: '📢',
            title: 'Active Notices',
            value: dashboardStats.activeNotices,
            subtitle: 'Published Notices',
            color: '#059669'
          },
          {
            icon: '🔬',
            title: 'Lab Equipment',
            value: dashboardStats.labEquipment,
            subtitle: 'Total Items',
            color: '#84cc16'
          },
          {
            icon: '📋',
            title: 'Pending Requisitions',
            value: dashboardStats.pendingRequisitions,
            subtitle: 'Awaiting Approval',
            color: '#ec4899'
          },
          {
            icon: '🎓',
            title: 'Active Internships',
            value: dashboardStats.internships,
            subtitle: 'Current Programs',
            color: '#06b6d4'
          }
        ].map((stat, index) => (
          <div key={index} style={{
            background: '#ffffff',
            padding: '2rem',
            borderRadius: '12px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            textAlign: 'center',
            transition: 'all 0.3s ease',
            cursor: 'pointer'
          }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
            }}>
            <div style={{
              fontSize: '2.5rem',
              color: stat.color,
              marginBottom: '1rem'
            }}>
              {stat.icon}
            </div>
            <h3 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>{stat.title}</h3>
            <p style={{
              fontSize: '2.2rem',
              fontWeight: 'bold',
              color: stat.color,
              margin: '0.5rem 0'
            }}>
              {stat.value}
            </p>
            <p style={{ color: '#666', margin: 0 }}>
              {stat.subtitle}
            </p>
          </div>
        ))}
      </section>

      {/* NEW DETAILED ATTENDANCE SECTION */}
      <section style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(350px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        {/* Student Attendance Card */}
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          overflow: 'hidden',
          transition: 'all 0.3s ease'
        }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
          }}>
          <div style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
            color: '#ffffff',
            padding: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <span style={{ fontSize: '2rem' }}>👥</span>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.3rem' }}>Student Attendance</h3>
              <p style={{ margin: 0, opacity: 0.9, fontSize: '0.9rem' }}>Today's Status</p>
            </div>
          </div>
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            {/* Pie Chart for Student Attendance */}
            <div style={{
              maxWidth: '300px',
              margin: '0 auto 1.5rem auto'
            }}>
              <Pie
                data={{
                  labels: ['Present', 'Absent'],
                  datasets: [{
                    data: [
                      dashboardStats.studentsPresent,
                      dashboardStats.totalStudentsToday - dashboardStats.studentsPresent
                    ],
                    backgroundColor: [
                      '#10b981', // Green for present
                      '#ef4444'  // Red for absent
                    ],
                    borderColor: [
                      '#ffffff',
                      '#ffffff'
                    ],
                    borderWidth: 2
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: true,
                  plugins: {
                    legend: {
                      position: 'bottom',
                      labels: {
                        padding: 15,
                        font: {
                          size: 12,
                          weight: 'bold'
                        }
                      }
                    },
                    tooltip: {
                      callbacks: {
                        label: function (context) {
                          const label = context.label || '';
                          const value = context.parsed || 0;
                          const total = dashboardStats.totalStudentsToday;
                          const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                          return `${label}: ${value} (${percentage}%)`;
                        }
                      }
                    }
                  }
                }}
              />
            </div>

            {/* Text Information */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '1rem'
            }}>
              <span style={{
                fontSize: '3rem',
                fontWeight: 'bold',
                color: '#3b82f6'
              }}>
                {dashboardStats.studentsPresent}
              </span>
              <span style={{
                fontSize: '1.5rem',
                color: '#666'
              }}>
                out of
              </span>
              <span style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                color: '#666'
              }}>
                {dashboardStats.totalStudentsToday}
              </span>
            </div>
            <p style={{
              color: '#666',
              margin: '0.5rem 0 1.5rem 0',
              fontSize: '1rem'
            }}>
              Students Present ({dashboardStats.studentAttendancePercentage}% of total enrolled)
            </p>
            <button
              onClick={() => navigateTo('Attendance Records')}
              style={{
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                transition: 'all 0.3s ease',
                width: '100%'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#2563eb';
                e.target.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#3b82f6';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              📊 View Full Student Attendance
            </button>
          </div>
        </div>

        {/* Teacher Attendance Card */}
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          overflow: 'hidden',
          transition: 'all 0.3s ease'
        }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
          }}>
          <div style={{
            background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
            color: '#ffffff',
            padding: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <span style={{ fontSize: '2rem' }}>👨‍🏫</span>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.3rem' }}>Teacher Attendance</h3>
              <p style={{ margin: 0, opacity: 0.9, fontSize: '0.9rem' }}>Today's Status</p>
            </div>
          </div>
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            {/* Pie Chart for Teacher Attendance */}
            <div style={{
              maxWidth: '300px',
              margin: '0 auto 1.5rem auto'
            }}>
              <Pie
                data={{
                  labels: ['Present', 'Absent'],
                  datasets: [{
                    data: [
                      dashboardStats.teachersPresent,
                      dashboardStats.totalTeachersToday - dashboardStats.teachersPresent
                    ],
                    backgroundColor: [
                      '#3b82f6', // Blue for present
                      '#f97316'  // Orange for absent
                    ],
                    borderColor: [
                      '#ffffff',
                      '#ffffff'
                    ],
                    borderWidth: 2
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: true,
                  plugins: {
                    legend: {
                      position: 'bottom',
                      labels: {
                        padding: 15,
                        font: {
                          size: 12,
                          weight: 'bold'
                        }
                      }
                    },
                    tooltip: {
                      callbacks: {
                        label: function (context) {
                          const label = context.label || '';
                          const value = context.parsed || 0;
                          const total = dashboardStats.totalTeachersToday;
                          const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                          return `${label}: ${value} (${percentage}%)`;
                        }
                      }
                    }
                  }
                }}
              />
            </div>

            {/* Text Information */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '1rem'
            }}>
              <span style={{
                fontSize: '3rem',
                fontWeight: 'bold',
                color: '#10b981'
              }}>
                {dashboardStats.teachersPresent}
              </span>
              <span style={{
                fontSize: '1.5rem',
                color: '#666'
              }}>
                out of
              </span>
              <span style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                color: '#666'
              }}>
                {dashboardStats.totalTeachersToday}
              </span>
            </div>
            <p style={{
              color: '#666',
              margin: '0.5rem 0 1.5rem 0',
              fontSize: '1rem'
            }}>
              Teachers Present ({dashboardStats.teacherAttendancePercentage}% of total faculty)
            </p>
            <button
              onClick={() => navigateTo('Teacher Attendance')}
              style={{
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                transition: 'all 0.3s ease',
                width: '100%'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#059669';
                e.target.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#10b981';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              📊 View Full Teacher Attendance
            </button>
          </div>
        </div>
      </section>

      {/* Separate Bar Graphs Section */}
      <section style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(350px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        {/* Student Bar Graph Card */}
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          overflow: 'hidden',
          transition: 'all 0.3s ease'
        }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
          }}>
          <div style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
            color: '#ffffff',
            padding: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <span style={{ fontSize: '2rem' }}>📊</span>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.3rem' }}>Student Attendance Graph</h3>
              <p style={{ margin: 0, opacity: 0.9, fontSize: '0.9rem' }}>Present vs Absent</p>
            </div>
          </div>
          <div style={{ padding: '2rem' }}>
            <div style={{
              maxWidth: '400px',
              margin: '0 auto'
            }}>
              <Bar
                data={{
                  labels: ['Present', 'Absent'],
                  datasets: [
                    {
                      label: 'Students',
                      data: [
                        dashboardStats.studentsPresent,
                        dashboardStats.totalStudentsToday - dashboardStats.studentsPresent
                      ],
                      backgroundColor: ['#3b82f6', '#93c5fd'], // Dark blue for present, Light blue for absent
                      borderColor: ['#2563eb', '#60a5fa'],
                      borderWidth: 2
                    }
                  ]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: true,
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        stepSize: 1,
                        font: {
                          size: 12
                        }
                      },
                      grid: {
                        color: 'rgba(0, 0, 0, 0.05)'
                      }
                    },
                    x: {
                      ticks: {
                        font: {
                          size: 14,
                          weight: 'bold'
                        }
                      },
                      grid: {
                        display: false
                      }
                    }
                  },
                  plugins: {
                    legend: {
                      display: false
                    },
                    tooltip: {
                      callbacks: {
                        label: function (context) {
                          const label = context.label || '';
                          const value = context.parsed.y || 0;
                          const total = dashboardStats.totalStudentsToday;
                          const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                          return `${label}: ${value} (${percentage}%)`;
                        }
                      },
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      padding: 12,
                      titleFont: {
                        size: 14,
                        weight: 'bold'
                      },
                      bodyFont: {
                        size: 13
                      }
                    }
                  }
                }}
              />
            </div>
          </div>
        </div>

        {/* Teacher Bar Graph Card */}
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          overflow: 'hidden',
          transition: 'all 0.3s ease'
        }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
          }}>
          <div style={{
            background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
            color: '#ffffff',
            padding: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <span style={{ fontSize: '2rem' }}>📊</span>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.3rem' }}>Teacher Attendance Graph</h3>
              <p style={{ margin: 0, opacity: 0.9, fontSize: '0.9rem' }}>Present vs Absent</p>
            </div>
          </div>
          <div style={{ padding: '2rem' }}>
            <div style={{
              maxWidth: '400px',
              margin: '0 auto'
            }}>
              <Bar
                data={{
                  labels: ['Present', 'Absent'],
                  datasets: [
                    {
                      label: 'Teachers',
                      data: [
                        dashboardStats.teachersPresent,
                        dashboardStats.totalTeachersToday - dashboardStats.teachersPresent
                      ],
                      backgroundColor: ['#10b981', '#6ee7b7'], // Dark green for present, Light green for absent
                      borderColor: ['#059669', '#34d399'],
                      borderWidth: 2
                    }
                  ]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: true,
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        stepSize: 1,
                        font: {
                          size: 12
                        }
                      },
                      grid: {
                        color: 'rgba(0, 0, 0, 0.05)'
                      }
                    },
                    x: {
                      ticks: {
                        font: {
                          size: 14,
                          weight: 'bold'
                        }
                      },
                      grid: {
                        display: false
                      }
                    }
                  },
                  plugins: {
                    legend: {
                      display: false
                    },
                    tooltip: {
                      callbacks: {
                        label: function (context) {
                          const label = context.label || '';
                          const value = context.parsed.y || 0;
                          const total = dashboardStats.totalTeachersToday;
                          const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                          return `${label}: ${value} (${percentage}%)`;
                        }
                      },
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      padding: 12,
                      titleFont: {
                        size: 14,
                        weight: 'bold'
                      },
                      bodyFont: {
                        size: 13
                      }
                    }
                  }
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* System Status & Quick Actions */}
      <section style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        {/* System Status */}
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#238548',
            color: '#ffffff',
            padding: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <span style={{ fontSize: '1.8rem' }}>🖥️</span>
            <h3 style={{ margin: 0, fontSize: '1.2rem' }}>System Status</h3>
          </div>
          <div style={{ padding: '1.5rem' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1rem',
              background: '#f8f9fa',
              borderRadius: '8px',
              marginBottom: '1rem'
            }}>
              <span style={{ fontWeight: 500, color: '#333' }}>Server Status</span>
              <span style={{
                padding: '0.25rem 0.75rem',
                borderRadius: '20px',
                fontSize: '0.8rem',
                fontWeight: 'bold',
                backgroundColor: '#e8f5e9',
                color: '#2e7d32'
              }}>
                {dashboardStats.serverStatus}
              </span>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1rem',
              background: '#f8f9fa',
              borderRadius: '8px'
            }}>
              <span style={{ fontWeight: 500, color: '#333' }}>Database</span>
              <span style={{
                padding: '0.25rem 0.75rem',
                borderRadius: '20px',
                fontSize: '0.8rem',
                fontWeight: 'bold',
                backgroundColor: '#e8f5e9',
                color: '#2e7d32'
              }}>
                {dashboardStats.dbStatus}
              </span>
            </div>
          </div>
        </div>

        {/* Quick Actions - UPDATED WITH TRAINING MANAGEMENT */}
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#238548',
            color: '#ffffff',
            padding: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <span style={{ fontSize: '1.8rem' }}>⚡</span>
            <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Quick Actions</h3>
          </div>
          <div style={{ padding: '1.5rem' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(2, 1fr)',
              gap: '1rem'
            }}>
              {[
                { label: 'Add Student', icon: '👥', action: 'Student Management' },
                { label: 'Add Teacher', icon: '👨‍🏫', action: 'Teacher Management' },
                { label: 'View Grievances', icon: '❗', action: 'Grievance Portal' },
                { label: 'Create Notice', icon: '📢', action: 'Notice Management' },
                { label: 'Manage Requisitions', icon: '📋', action: 'Requisition' },

                { label: 'Manage Subjects', icon: '📚', action: 'Manage Subjects' },
                { label: 'Training Management', icon: '🎯', action: 'Training Management' } // ⭐ NEW ADDITION
              ].map((action, index) => (
                <button
                  key={index}
                  onClick={() => navigateTo(action.action)}
                  style={{
                    padding: '1rem',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    backgroundColor: '#f8fafc',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    textAlign: 'center',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#374151',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#238548';
                    e.target.style.color = 'white';
                    e.target.style.borderColor = '#238548';
                    e.target.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#f8fafc';
                    e.target.style.color = '#374151';
                    e.target.style.borderColor = '#e2e8f0';
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  <span style={{ fontSize: '1.5rem' }}>{action.icon}</span>
                  {action.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Recent Activities & System Alerts */}
      <section style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '1.5rem'
      }}>
        {/* Recent Activities */}
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#238548',
            color: '#ffffff',
            padding: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <span style={{ fontSize: '1.8rem' }}>📈</span>
            <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Recent Activities</h3>
          </div>
          <div style={{ padding: '1.5rem' }}>
            {recentActivities.length > 0 ? recentActivities.map((activity, index) => (
              <div key={index} style={{
                padding: '1rem',
                borderBottom: index < recentActivities.length - 1 ? '1px solid #ecf0f1' : 'none',
                transition: 'all 0.3s ease'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem'
                }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: activity.priority === 'high' ? '#ef4444' : '#238548'
                  }}></div>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '0.9rem' }}>
                      {activity.message}
                    </p>
                    <p style={{ margin: 0, color: '#999', fontSize: '0.8rem' }}>
                      {formatDate(activity.timestamp)}
                    </p>
                  </div>
                </div>
              </div>
            )) : (
              <p style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>
                No recent activities
              </p>
            )}
          </div>
        </div>

        {/* System Alerts */}
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#238548',
            color: '#ffffff',
            padding: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <span style={{ fontSize: '1.8rem' }}>🚨</span>
            <h3 style={{ margin: 0, fontSize: '1.2rem' }}>System Alerts</h3>
          </div>
          <div style={{ padding: '1.5rem' }}>
            {systemAlerts.length > 0 ? systemAlerts.map((alert, index) => (
              <div key={index} style={{
                padding: '1rem',
                borderBottom: index < systemAlerts.length - 1 ? '1px solid #ecf0f1' : 'none',
                transition: 'all 0.3s ease'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem'
                }}>
                  <div style={{
                    fontSize: '1.2rem',
                    color: alert.type === 'error' ? '#ef4444' : alert.type === 'warning' ? '#f59e0b' : '#10b981'
                  }}>
                    {alert.type === 'error' ? '❌' : alert.type === 'warning' ? '⚠️' : '✅'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '0.9rem' }}>
                      {alert.message}
                    </p>
                    <p style={{ margin: 0, color: '#999', fontSize: '0.8rem' }}>
                      {formatDate(alert.timestamp)}
                    </p>
                  </div>
                </div>
              </div>
            )) : (
              <div style={{
                textAlign: 'center',
                color: '#666',
                padding: '2rem'
              }}>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✅</div>
                <p>All systems operational</p>
              </div>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default AdminDashboard;